import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import { SkipLink } from './components/ui';
import ScrollToTop from './helper/ScrollToTop';
import RouteAnnouncer from './helper/RouteAnnouncer';
import BackToTop from './helper/BackToTop';
import useReveal from './helper/useReveal';
import { legacyRedirects } from './data/navigation';

/* Home is eager — it is the entry point for most visits and lazy-loading it
   only adds a spinner to the first paint. Everything else is split. */
import Home from './pages/Home';

const About = lazy(() => import('./pages/About'));
const Capabilities = lazy(() => import('./pages/Capabilities'));
const CapabilityDetail = lazy(() => import('./pages/CapabilityDetail'));
const LscNexus = lazy(() => import('./pages/LscNexus'));
const Industries = lazy(() => import('./pages/Industries'));
const PastPerformance = lazy(() => import('./pages/PastPerformance'));
const CapabilityStatement = lazy(() => import('./pages/CapabilityStatement'));
const Contact = lazy(() => import('./pages/Contact'));
const Accessibility = lazy(() => import('./pages/Accessibility'));
const NotFound = lazy(() => import('./pages/NotFound'));

const RouteFallback = () => (
  <div className="route-fallback" role="status">
    <span className="sr-only">Loading page</span>
    <span className="route-fallback__spinner" aria-hidden="true" />
  </div>
);

/* Inside the router so it can re-scan on navigation */
const RevealDriver = () => {
  useReveal();
  return null;
};

const App = () => (
  <HelmetProvider>
    <BrowserRouter>
      <ScrollToTop />
      <RouteAnnouncer />
      <RevealDriver />
      <SkipLink />

      <Header />

      {/* tabIndex -1 so RouteAnnouncer can move focus here after navigation
          without making the region itself tabbable. */}
      <main id="main-content" tabIndex={-1}>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about-us" element={<About />} />

            <Route path="/capabilities" element={<Capabilities />} />
            <Route path="/capabilities/:slug" element={<CapabilityDetail />} />

            <Route path="/lscnexus" element={<LscNexus />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/past-performance" element={<PastPerformance />} />
            <Route path="/capability-statement" element={<CapabilityStatement />} />
            <Route path="/contact-us" element={<Contact />} />
            <Route path="/accessibility" element={<Accessibility />} />

            {/* Legacy URLs from the previous site — inbound links and any
                indexed pages resolve instead of 404ing. */}
            {legacyRedirects.map(({ from, to }) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      <Footer />
      <BackToTop />
    </BrowserRouter>
  </HelmetProvider>
);

export default App;
